import type { VaultProvider } from '../vaults/fetchVaultsAll'
import type { VaultLookupEntry } from '../vaults/lookup'
import type { Denomination, YieldProfile } from '../vaults/classification'
import type { LenderDataEntry } from '../lending/user-data/summary/types'
import type { BaseLendingPositions } from '../types/lenderTypes'
import { buildVaultEarnUid, isVaultVenue, type EarnVenueKind } from './uid'
import type {
  EarnAvailability,
  EarnCapability,
  EarnExit,
  EarnMarket,
  EarnRate,
} from './types'

/**
 * `EarnPosition` — one row of a user's supply-side portfolio, from either half
 * of the stack.
 *
 * The user half of `/v1/data/earn`. Where `EarnMarket` answers "what can I
 * deposit into", this answers "what do I hold" — and it is deliberately NOT
 * symmetric with it, because the two halves of the stack carry positions at
 * different granularities and flattening that difference would be a lie:
 *
 * ```
 * vault    →  ONE ROW PER VAULT.       A share balance is a standalone position.
 * lending  →  ONE ROW PER (chain, lender).  A cross-margin account is ONE
 *             position — its markets are legs of a single solvency
 *             calculation, not independent deposits.
 * ```
 *
 * Splitting a cross-margin account into per-market rows is the failure this
 * shape exists to prevent: it renders a $100 supply against a $90 debt as two
 * unrelated $100 and $90 rows, publishes a health factor per leg that does not
 * exist, and lets a UI sum a column that was never additive. The legs are
 * still present — on {@link EarnLendingPosition.legs}, each pointing back at
 * its catalogue row — but the ROW is the account.
 *
 * See EARN_ENDPOINT_PLAN.md §7.
 */

/**
 * Row identity. **This is NOT an `earnUid`** and must never be passed to an
 * action route.
 *
 * A vault position's `positionUid` happens to equal its `earnUid` — one vault
 * is one market is one position. A lending position has no `earnUid` at all:
 * it spans every market in the account, so no single market uid identifies it.
 * Its uid is deliberately TWO segments (`<LENDER>:<chainId>`), which
 * `parseEarnUid` rejects — so a caller that confuses the two fails at the edge
 * instead of routing a withdrawal to whichever market sorted first.
 *
 * To act on a lending position, take the `earnUid` off the individual
 * {@link EarnPositionLeg}.
 */
export type EarnPositionUid = string

/** `AAVE_V3` + `1` → `AAVE_V3:1`. Two segments, by design — see above. */
export function buildLendingPositionUid(
  lender: string,
  chainId: string,
): EarnPositionUid {
  if (!lender || !chainId) {
    throw new Error(
      `Invalid lending positionUid parameters: lender=${lender}, chainId=${chainId}`,
    )
  }
  if (isVaultVenue(lender)) {
    throw new Error(
      `Lender '${lender}' uses the reserved vault venue namespace`,
    )
  }
  return `${lender}:${chainId}`
}

export interface EarnPositionAsset {
  address: string
  symbol?: string
  decimals?: number
  /** Unit price in USD. `0` ⇒ unpriced, NOT worthless. */
  priceUsd?: number
  /**
   * Token icon, where the lender metadata resolved one.
   *
   * Carried on the leg so a consumer does not need a token list loaded just to
   * label a position it was already handed — the address alone identifies
   * nothing to a reader.
   */
  logoURI?: string
}

/** Fields both halves carry, so a table can render one row type. */
export interface EarnPositionBase {
  positionUid: EarnPositionUid
  chainId: string
  /** `AAVE_V3` (a `Lender` key) or `vault.savings` (`vault.<provider>`). */
  venue: string
  venueKind: EarnVenueKind
  /** Display label — curator where one exists, else protocol. */
  brand?: string
  name?: string
  logoURI?: string
  /** USD value of everything supplied. */
  suppliedUsd: number
  /** USD value of everything borrowed. Always `0` on the vault half. */
  borrowedUsd: number
  /** `suppliedUsd - borrowedUsd` — what the position is actually worth. */
  netUsd: number
  /**
   * Net APR on the position AS HELD, in PERCENT — deposit yield less borrow
   * cost, over `netUsd`. NOT the market's headline rate: a 2x loop on a 4 %
   * market reads ~8 % here and 4 % on the catalogue row.
   *
   * Absent ⇒ not computable, which is not the same as zero.
   */
  apr?: number
}

/**
 * One market inside a lending position.
 *
 * `earnUid` is the join back to `/v1/data/earn` — present whenever the lender
 * minted a well-formed `marketUid`, absent rather than reconstructed when it
 * did not (a rebuilt uid routes to the wrong market for Compound V2 and
 * Dolomite; see `earnUidFromMarketUid`).
 */
export interface EarnPositionLeg {
  /** Catalogue join key. Absent ⇒ this leg has no addressable market row. */
  earnUid?: string
  marketUid: string
  /** Present ⇒ the leg is bound to one loan (fixed-term lenders). */
  loanId?: string
  asset: EarnPositionAsset
  /**
   * Which side of the book this leg sits on.
   *
   * **`'none'` is the common case and the important one**: lenders report every
   * market the account is CONFIGURED in, not just the ones it holds something
   * in — an Aave V4 account with one USDC debt reports ten legs, nine of them
   * empty. Marking them here rather than letting each consumer re-derive it is
   * what stops a UI rendering nine empty markets as nine positions, which is
   * indistinguishable from nine real ones at a glance.
   *
   * Empty legs are KEPT rather than dropped — "markets this account is set up
   * in" is a real question — but nothing may present them as holdings.
   */
  side: 'supply' | 'borrow' | 'both' | 'none'
  deposits: string
  depositsUsd: number
  debt: string
  debtUsd: number
  collateralEnabled: boolean
  /** Max withdrawable in token units, where the lender reports it. */
  withdrawable?: string
}

/**
 * The three legs of a position's yield, each already expressed over NAV and in
 * PERCENT, so they simply add.
 *
 * **They are separate fields upstream and none of them contains another.**
 * `aprData.apr` is `(depositInterest − borrowInterest) / nav` — market interest
 * ONLY. `rewardApr` and `intrinsicApr` are computed alongside it over the same
 * denominator and are omitted from it entirely. Reading `aprData.apr` as "the
 * net APR" is therefore wrong for exactly the positions where it matters most:
 * a levered carry trade borrows a cheap asset to hold a yield-bearing one, so
 * the market leg is the COST side and the asset's own yield — the entire
 * reason for the trade — lands in `intrinsicApr`. A 22x sDOLA/crvUSD loop
 * reports about −74 % on the market leg alone and a large positive number once
 * the collateral's own yield is counted.
 *
 * Kept as a breakdown rather than folded into one number so that a headline can
 * never quietly become un-inspectable: emissions can stop, and an intrinsic
 * yield is a different promise from an interest rate.
 */
export interface EarnAprBreakdown {
  /** Deposit interest less borrow interest, over NAV. */
  market: number
  /** Incentive emissions, over NAV. Can stop. */
  rewards: number
  /**
   * The yield the ASSETS carry themselves (sDOLA, sfrxUSD, an LST) net of the
   * yield accruing on whatever was borrowed, over NAV.
   */
  intrinsic: number
}

/** A sub-account within a lender, for the lenders that have more than one. */
export interface EarnPositionSubAccount {
  accountId: string
  health: number | null
  suppliedUsd: number
  borrowedUsd: number
  netUsd: number
  legs: EarnPositionLeg[]
}

/**
 * A whole lending account on one lender, on one chain — ONE row however many
 * markets it touches.
 */
export interface EarnLendingPosition extends EarnPositionBase {
  venueKind: 'lending'
  lender: string
  account: string
  /**
   * Health factor of the account. Only meaningful when the lender is
   * cross-margin (`subAccounts.length <= 1`); otherwise `null`, with each
   * sub-account carrying its own. `null` also means "no debt, so no health".
   */
  health: number | null
  /** `deposits / nav`. `1` ⇒ unlevered, `0` ⇒ not computable. */
  leverage: number
  /**
   * What `apr` is made of. `market + rewards + intrinsic === apr`, so a
   * consumer can show the split without re-deriving it — and can see when a
   * headline rests entirely on emissions or entirely on collateral yield.
   */
  aprBreakdown: EarnAprBreakdown
  /** MARKET deposit interest only — see `aprBreakdown` for the other legs. */
  depositApr: number
  /** MARKET borrow interest only, as a positive cost. */
  borrowApr: number
  /**
   * TRUE when the whole position is one solvency calculation, i.e. this row is
   * the complete picture. FALSE ⇒ read `subAccounts`, and do not present
   * `health` as the account's.
   */
  crossMargin: boolean
  /** Every market leg, flattened across sub-accounts. */
  legs: EarnPositionLeg[]
  subAccounts: EarnPositionSubAccount[]
  /**
   * Some of this lender's reads did not complete. The legs are real but the
   * set is a LOWER BOUND — `netUsd`, `apr` and `health` must not be rendered
   * as fact. Carried straight through from `/lending/user-positions`.
   */
  incomplete?: boolean
  /** Served from the last complete snapshot, `staleAgeMs` ago. */
  stale?: boolean
  staleAgeMs?: number
}

/** A share balance in one vault — a standalone position. */
export interface EarnVaultPosition extends EarnPositionBase {
  venueKind: 'vault'
  /**
   * The catalogue row. Unlike the lending half this is always present and
   * always actionable — pass it straight to an earn action route.
   */
  earnUid: string
  provider: VaultProvider
  /** Share-token address. */
  vault: string
  asset: EarnPositionAsset
  /** Raw share balance, base units of `shareDecimals`. */
  sharesRaw: string
  shares: string
  /** Share balance converted to underlying at the fair share price. */
  assetsRaw: string
  assets: string
  /** Share-token decimals. Differs from the asset's for Lagoon. */
  shareDecimals: number
  yieldProfile?: YieldProfile
  denomination?: Denomination

  // --- Enrichment from the catalogue row, joined on `earnUid` ---------------
  //
  // Carried on the position rather than left to the caller so a held row can
  // render its own rate and its own withdraw CTA. A client cannot do this join
  // itself: its copy of the catalogue is FILTERED (by TVL floor, risk score,
  // pass-through, facet selection), and the one row it is most likely to be
  // missing is a vault the user is still holding after it was filtered out.
  //
  // All four are absent when the market did not resolve — the balance is still
  // exact, only the deal around it is unknown.

  /** What the venue pays, PERCENT. */
  rate?: EarnRate
  /** How the money gets out. */
  exit?: EarnExit
  /** Whether it can be entered right now, and why not. */
  availability?: EarnAvailability
  /** What can be done with the position — drives the withdraw CTA. */
  capabilities?: EarnCapability[]
}

export type EarnPosition = EarnLendingPosition | EarnVaultPosition

export function isVaultPosition(p: EarnPosition): p is EarnVaultPosition {
  return p.venueKind === 'vault'
}

export function isLendingPosition(p: EarnPosition): p is EarnLendingPosition {
  return p.venueKind === 'lending'
}

/** Per-source health, so a dead half degrades the list rather than the route. */
export interface EarnPositionSourceStatus {
  source: 'lending' | 'vaults'
  status: 'ok' | 'degraded' | 'failed'
  /** Rows contributed by this source. */
  rows: number
  /** Present when not `ok`. */
  error?: string
}

export interface EarnPositionTotals {
  suppliedUsd: number
  borrowedUsd: number
  netUsd: number
  /** `netUsd` of the lending half alone. */
  lendingUsd: number
  /** `netUsd` of the vault half alone. */
  vaultUsd: number
}

/**
 * `/v1/data/earn/positions` response. Same contract as `/v1/data/earn`: the
 * shape never changes, a degraded source is reported in `sources[]` with
 * whatever did resolve still served.
 */
export interface EarnPositionsResponse {
  ok: boolean
  account: string
  chainIds: string[]
  count: number
  /** Always `'percent'`, stamped so no consumer has to guess. */
  rateUnit: 'percent'
  items: EarnPosition[]
  totals: EarnPositionTotals
  sources: EarnPositionSourceStatus[]
  /** Set when any lending entry was `incomplete` — totals are a lower bound. */
  partial?: boolean
  /** Set when any entry was served from a last-known-good snapshot. */
  stale?: boolean
}

// ---------------------------------------------------------------------------
// Normalizers
// ---------------------------------------------------------------------------

/**
 * A `marketUid` is an `earnUid` verbatim — but only when it is well formed.
 *
 * Returns undefined rather than throwing or reconstructing: a leg with an
 * unusable uid still shows its balance, it just carries no catalogue join.
 */
function legEarnUid(marketUid: string | undefined): string | undefined {
  if (!marketUid) return undefined
  const parts = marketUid.split(':')
  if (parts.length !== 3 || parts.some((p) => !p)) return undefined
  if (isVaultVenue(parts[0])) return undefined
  return marketUid
}

function num(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

/** Pull asset identity out of the loosely-typed `underlyingInfo` blob. */
function legAsset(pos: BaseLendingPositions): EarnPositionAsset {
  const asset = (pos.underlyingInfo?.asset ?? {}) as Record<string, unknown>
  const address =
    typeof asset.address === 'string'
      ? asset.address.toLowerCase()
      : // Fall back to the uid's third segment, which IS the underlying for
        // the default-format lenders and merely a harmless label for the rest.
        (pos.marketUid?.split(':')[2] ?? '')
  const decimals =
    typeof asset.decimals === 'number' ? asset.decimals : undefined
  const symbol = typeof asset.symbol === 'string' ? asset.symbol : undefined
  // `AssetInfo.logoURI` is nullable, so guard on the string rather than
  // spreading a `null` a consumer would have to re-check.
  const logoURI = typeof asset.logoURI === 'string' ? asset.logoURI : undefined
  return {
    address,
    symbol,
    decimals,
    ...(logoURI ? { logoURI } : {}),
  }
}

function toLeg(pos: BaseLendingPositions): EarnPositionLeg {
  const depositsUsd = num(pos.depositsUSD)
  // Stable and variable debt are one obligation to a user looking at a row.
  const debtUsd = num(pos.debtUSD) + num(pos.debtStableUSD)
  const hasSupply = depositsUsd !== 0 || num(pos.deposits) !== 0
  const hasDebt =
    debtUsd !== 0 || num(pos.debt) !== 0 || num(pos.debtStable) !== 0
  return {
    earnUid: legEarnUid(pos.marketUid),
    marketUid: pos.marketUid,
    ...(pos.loanId ? { loanId: pos.loanId } : {}),
    asset: legAsset(pos),
    // An empty market is `'none'`, NOT `'supply'` — the old fallback quietly
    // reported every market the account was merely configured in as a holding.
    side:
      hasSupply && hasDebt
        ? 'both'
        : hasDebt
          ? 'borrow'
          : hasSupply
            ? 'supply'
            : 'none',
    deposits: pos.deposits ?? '0',
    depositsUsd,
    debt: pos.debt ?? '0',
    debtUsd,
    collateralEnabled: pos.collateralEnabled ?? false,
    ...(pos.withdrawable !== undefined
      ? { withdrawable: pos.withdrawable }
      : {}),
  }
}

/**
 * Positions the ACCOUNT holds, without double-counting brokered loans.
 *
 * A leg carrying a `loanId` is one slice of a fixed-term loan that the lender
 * ALSO reports in aggregate on the unbound leg (Lista's LendingBroker, the
 * Exactly fixed pools). Summing both inflates the position. Legs are kept for
 * display but excluded from the totals — the same rule `collectSubAccountPositions`
 * applies in the allocator.
 */
function isCountable(leg: EarnPositionLeg): boolean {
  return leg.loanId === undefined
}

/**
 * `LenderDataEntry` → ONE `EarnLendingPosition`.
 *
 * The entry is already aggregated per (chain, lender) by `buildSummaries`, so
 * this is a projection, not a re-summation — the USD figures come off
 * `balanceData`, which the summary computed from the same legs. The legs are
 * flattened purely so a row can show what it is made of.
 */
export function earnPositionFromLenderEntry(
  entry: LenderDataEntry,
): EarnLendingPosition {
  const subs = entry.data ?? []

  const subAccounts: EarnPositionSubAccount[] = subs.map((sub) => {
    const legs = (sub.positions ?? []).map(toLeg)
    const counted = legs.filter(isCountable)
    const suppliedUsd = counted.reduce((a, l) => a + l.depositsUsd, 0)
    const borrowedUsd = counted.reduce((a, l) => a + l.debtUsd, 0)
    return {
      accountId: sub.accountId,
      health: sub.health ?? null,
      suppliedUsd,
      borrowedUsd,
      netUsd: suppliedUsd - borrowedUsd,
      legs,
    }
  })

  // A sub-account with neither a position nor a balance is plumbing, not a
  // position — a lender that pre-allocates sub-accounts (Euler) would
  // otherwise report a dozen empty ones and make every row look multi-account.
  //
  // The test is HELD legs, not legs: a sub-account configured in ten markets
  // and holding nothing in any of them passed a bare `legs.length > 0` and
  // made a plain cross-margin account render as multi-account.
  const active = subAccounts.filter(
    (s) => s.legs.some((l) => l.side !== 'none') || s.netUsd !== 0,
  )
  const crossMargin = active.length <= 1

  const suppliedUsd = num(entry.balanceData?.deposits)
  const borrowedUsd = num(entry.balanceData?.debt)

  // THE THREE LEGS ADD. `aprData.apr` is the market leg alone; taking it as the
  // net APR reports a levered carry trade as a large loss, because the market
  // leg IS the cost side of that trade and the collateral's own yield — the
  // whole point of it — sits in `intrinsicApr`. All three are computed over the
  // same NAV denominator and nav-weighted identically by `buildSummaries`, so
  // summing them needs no re-derivation from deposits and debt.
  const aprBreakdown: EarnAprBreakdown = {
    market: num(entry.aprData?.apr),
    rewards: num(entry.aprData?.rewardApr),
    intrinsic: num(entry.aprData?.intrinsicApr),
  }

  return {
    positionUid: buildLendingPositionUid(entry.lender, entry.chainId),
    chainId: entry.chainId,
    venue: entry.lender,
    venueKind: 'lending',
    lender: entry.lender,
    account: entry.account,
    brand: entry.lenderInfo?.name,
    name: entry.lenderInfo?.name,
    ...(entry.lenderInfo?.logoUri ? { logoURI: entry.lenderInfo.logoUri } : {}),
    suppliedUsd,
    borrowedUsd,
    netUsd: num(entry.balanceData?.nav),
    apr: aprBreakdown.market + aprBreakdown.rewards + aprBreakdown.intrinsic,
    aprBreakdown,
    depositApr: num(entry.aprData?.depositApr),
    borrowApr: num(entry.aprData?.borrowApr),
    // Only a cross-margin account HAS one health factor. Publishing the first
    // sub-account's as the row's would be a number about a different position.
    health: crossMargin ? (active[0]?.health ?? null) : null,
    leverage: num(entry.leverage),
    crossMargin,
    legs: subAccounts.flatMap((s) => s.legs),
    subAccounts: active,
    ...(entry.incomplete ? { incomplete: true } : {}),
    ...(entry.stale ? { stale: true, staleAgeMs: entry.staleAgeMs } : {}),
  }
}

/** What a caller must supply per vault beyond the cached public metadata. */
export interface VaultBalanceInput {
  /** Raw share balance from `balanceOf(account)`. */
  sharesRaw: bigint
  /** Underlying unit price in USD. `0` ⇒ unpriced. */
  priceUsd?: number
  /**
   * The catalogue row for this vault, where one resolved. Supplies the rate,
   * the exit and the capabilities — everything about the DEAL, as opposed to
   * the balance. Absent ⇒ those fields are omitted rather than defaulted; a
   * missing sheet reads as "unknown", never as "instant, free, 0 %".
   */
  market?: EarnMarket
}

/**
 * ERC-4626 convention: `assets = shares * totalAssets / totalSupply`.
 *
 * Returns `0n` for an empty vault or zero shares — both safe for display, and
 * both distinct from an error.
 */
export function vaultSharesToAssets(
  sharesRaw: bigint,
  meta: Pick<VaultLookupEntry, 'totalAssets' | 'totalSupply'>,
): bigint {
  if (sharesRaw === 0n) return 0n
  const totalSupply = BigInt(meta.totalSupply || '0')
  if (totalSupply === 0n) return 0n
  return (sharesRaw * BigInt(meta.totalAssets || '0')) / totalSupply
}

/**
 * `VaultLookupEntry` + a share balance → ONE `EarnVaultPosition`.
 *
 * `format` is injected rather than importing viem here so this stays a pure
 * transform the worker and the tests can both drive; pass `formatUnits`.
 */
export function earnPositionFromVaultBalance(
  meta: VaultLookupEntry,
  chainId: string,
  input: VaultBalanceInput,
  format: (value: bigint, decimals: number) => string,
): EarnVaultPosition {
  const assetsRaw = vaultSharesToAssets(input.sharesRaw, meta)
  // Shares carry the SHARE token's decimals; the converted assets carry the
  // UNDERLYING's, and for Lagoon those differ (18-dec shares over 6-dec USDC).
  // Formatting both with one number is off by 1e12.
  const assetDecimals = meta.assetDecimals ?? meta.decimals
  const assets = format(assetsRaw, assetDecimals)
  const priceUsd = input.priceUsd ?? 0
  const suppliedUsd = priceUsd * parseFloat(assets)
  const market = input.market
  const uid = buildVaultEarnUid(meta.provider, chainId, meta.address)

  return {
    positionUid: uid,
    earnUid: uid,
    chainId,
    venue: `vault.${meta.provider}`,
    venueKind: 'vault',
    provider: meta.provider,
    vault: meta.address,
    // The catalogue's identity resolution (curator, brand, branded icon) beats
    // the raw share-token name where it resolved.
    name: market?.name ?? meta.name,
    ...(market?.brand ? { brand: market.brand } : {}),
    ...((market?.logoURI ?? meta.logoURI)
      ? { logoURI: market?.logoURI ?? meta.logoURI }
      : {}),
    asset: {
      address: meta.underlying,
      ...(market?.asset.symbol ? { symbol: market.asset.symbol } : {}),
      decimals: assetDecimals,
      priceUsd,
    },
    sharesRaw: input.sharesRaw.toString(),
    shares: format(input.sharesRaw, meta.decimals),
    assetsRaw: assetsRaw.toString(),
    assets,
    shareDecimals: meta.decimals,
    suppliedUsd,
    // A vault share is a supply-only instrument — there is no borrow leg to
    // report, and `0` here is a fact rather than a missing read.
    borrowedUsd: 0,
    netUsd: suppliedUsd,
    // For a vault the position's APR IS the venue's rate — there is no borrow
    // leg to net off, so unlike the lending half the two never diverge.
    ...(market?.rate?.total !== undefined ? { apr: market.rate.total } : {}),
    ...(market?.rate ? { rate: market.rate } : {}),
    ...(market?.exit ? { exit: market.exit } : {}),
    ...(market?.availability ? { availability: market.availability } : {}),
    ...(market?.capabilities ? { capabilities: market.capabilities } : {}),
    ...(meta.yieldProfile ? { yieldProfile: meta.yieldProfile } : {}),
    ...(meta.denomination ? { denomination: meta.denomination } : {}),
  }
}

/** Portfolio totals across both halves. */
export function earnPositionTotals(items: EarnPosition[]): EarnPositionTotals {
  let suppliedUsd = 0
  let borrowedUsd = 0
  let lendingUsd = 0
  let vaultUsd = 0
  for (const it of items) {
    suppliedUsd += it.suppliedUsd
    borrowedUsd += it.borrowedUsd
    if (it.venueKind === 'vault') vaultUsd += it.netUsd
    else lendingUsd += it.netUsd
  }
  return {
    suppliedUsd,
    borrowedUsd,
    netUsd: suppliedUsd - borrowedUsd,
    lendingUsd,
    vaultUsd,
  }
}
